import { useEffect, useState } from "react";
import { toast } from "sonner";
import { adminLogin, listRegistrations, listContactMessages, getAdminStats } from "@/lib/api";
import { LondonListPanel } from "@/components/admin/LondonListPanel";
import { LogOut, Users, MessageSquare, BarChart3, Loader2 } from "lucide-react";

const TOKEN_KEY = "bot_admin_token";

export default function Admin() {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY) || "");
  const [input, setInput] = useState("");
  const [authed, setAuthed] = useState(false);
  const [loading, setLoading] = useState(false);
  const [tab, setTab] = useState("registrations");
  const [stats, setStats] = useState(null);
  const [registrations, setRegistrations] = useState([]);
  const [messages, setMessages] = useState([]);
  const [londonCount, setLondonCount] = useState(null);

  const fetchAll = async (t) => {
    setLoading(true);
    try {
      const [s, regs, msgs] = await Promise.all([
        getAdminStats(t),
        listRegistrations(t),
        listContactMessages(t),
      ]);
      setStats(s);
      setRegistrations(regs);
      setMessages(msgs);
      setAuthed(true);
    } catch (err) {
      localStorage.removeItem(TOKEN_KEY);
      setToken("");
      setAuthed(false);
      toast.error("Invalid or expired token.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (token) fetchAll(token);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onLogin = async (e) => {
    e.preventDefault();
    try {
      await adminLogin(input);
      localStorage.setItem(TOKEN_KEY, input);
      setToken(input);
      await fetchAll(input);
      toast.success("Welcome, admin.");
    } catch {
      toast.error("Invalid admin token.");
    }
  };

  const onLogout = () => {
    localStorage.removeItem(TOKEN_KEY);
    setToken("");
    setAuthed(false);
    setInput("");
    toast.success("Logged out.");
  };

  if (!authed) {
    return (
      <div className="min-h-screen bg-zinc-50 flex items-center justify-center p-6">
        <form
          onSubmit={onLogin}
          data-testid="admin-login-form"
          className="w-full max-w-md bg-white border border-zinc-200 p-8"
        >
          <div className="overline text-[#FF2A00]">/ BoT Summit · Admin</div>
          <h1 className="font-display text-3xl uppercase tracking-tight mt-4">
            Restricted Access
          </h1>
          <p className="text-sm text-zinc-600 mt-2">
            Enter the admin token to view registrations and messages.
          </p>
          <label className="overline text-zinc-500 mt-8 mb-2 block">Admin Token</label>
          <input
            data-testid="admin-token-input"
            type="password"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder="••••••••••••"
            className="w-full border border-zinc-300 px-4 py-3 text-sm focus:outline-none focus:border-[#002FA7] focus:ring-1 focus:ring-[#002FA7]"
            required
          />
          <button
            type="submit"
            data-testid="admin-login-submit"
            className="w-full mt-6 bg-[#002FA7] text-white px-7 py-4 text-xs font-bold tracking-[0.25em] uppercase hover:bg-black transition-colors"
          >
            Enter Dashboard →
          </button>
          <p className="mt-6 text-xs text-zinc-500">
            Back to <a href="/" className="underline hover:text-[#002FA7]">landing page</a>.
          </p>
        </form>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-zinc-50">
      <header className="border-b border-zinc-200 bg-white sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <span className="w-9 h-9 bg-[#002FA7] text-white font-display text-sm flex items-center justify-center">
              BoT
            </span>
            <div>
              <div className="font-display text-base tracking-tight">Admin Dashboard</div>
              <div className="overline text-zinc-500 text-[10px]">BoT Summit 2026</div>
            </div>
          </div>
          <button
            data-testid="admin-logout"
            onClick={onLogout}
            className="inline-flex items-center gap-2 text-xs font-bold tracking-[0.2em] uppercase border border-zinc-300 px-4 py-2 hover:bg-black hover:text-white hover:border-black"
          >
            <LogOut size={14} /> Logout
          </button>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 md:px-10 py-10">
        {/* Stats */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 border border-zinc-200 bg-white">
            <StatCell label="Registrations" value={stats.total_registrations} icon={Users} />
            <StatCell label="Messages" value={stats.total_contact_messages} icon={MessageSquare} />
            <StatCell label="Delegates" value={stats.by_ticket_type?.delegate || 0} icon={BarChart3} />
            <StatCell label="Students" value={stats.by_ticket_type?.student || 0} icon={BarChart3} last />
          </div>
        )}

        {/* Tabs */}
        <div className="mt-10 flex border-b border-zinc-200">
          <TabBtn active={tab === "registrations"} onClick={() => setTab("registrations")} testid="tab-registrations">
            Registrations ({registrations.length})
          </TabBtn>
          <TabBtn active={tab === "messages"} onClick={() => setTab("messages")} testid="tab-messages">
            Messages ({messages.length})
          </TabBtn>
          <TabBtn active={tab === "london"} onClick={() => setTab("london")} testid="tab-london">
            London List{londonCount !== null ? ` (${londonCount})` : ""}
          </TabBtn>
          <button
            onClick={() => fetchAll(token)}
            data-testid="admin-refresh"
            className="ml-auto overline text-zinc-600 hover:text-[#002FA7] px-3"
          >
            {loading ? <Loader2 size={14} className="animate-spin inline" /> : "↻ Refresh"}
          </button>
        </div>

        {/* Content */}
        <div className="mt-6 bg-white border border-zinc-200 overflow-x-auto">
          {tab === "london" ? (
            <LondonListPanel token={token} onCount={setLondonCount} />
          ) : tab === "registrations" ? (
            <table className="w-full text-sm" data-testid="registrations-table">
              <thead className="bg-zinc-50 border-b border-zinc-200">
                <tr className="overline text-zinc-500">
                  <Th>#</Th><Th>Name</Th><Th>Email</Th><Th>Phone</Th>
                  <Th>Organization</Th><Th>Role</Th><Th>Ticket</Th><Th>Date</Th>
                </tr>
              </thead>
              <tbody>
                {registrations.length === 0 ? (
                  <tr><td colSpan={8} className="p-8 text-center text-zinc-500">No registrations yet.</td></tr>
                ) : (
                  registrations.map((r, i) => (
                    <tr key={r.id} data-testid={`reg-row-${i}`} className="border-b border-zinc-100 hover:bg-zinc-50">
                      <Td className="text-zinc-500">{i + 1}</Td>
                      <Td className="font-medium">{r.full_name}</Td>
                      <Td>{r.email}</Td>
                      <Td>{r.phone}</Td>
                      <Td>{r.organization}</Td>
                      <Td>{r.role}</Td>
                      <Td><span className="overline bg-[#002FA7]/10 text-[#002FA7] px-2 py-1">{r.ticket_type}</span></Td>
                      <Td className="text-zinc-500 font-mono text-xs">{new Date(r.created_at).toLocaleString()}</Td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          ) : (
            <table className="w-full text-sm" data-testid="messages-table">
              <thead className="bg-zinc-50 border-b border-zinc-200">
                <tr className="overline text-zinc-500">
                  <Th>#</Th><Th>Name</Th><Th>Email</Th><Th>Subject</Th><Th>Message</Th><Th>Date</Th>
                </tr>
              </thead>
              <tbody>
                {messages.length === 0 ? (
                  <tr><td colSpan={6} className="p-8 text-center text-zinc-500">No messages yet.</td></tr>
                ) : (
                  messages.map((m, i) => (
                    <tr key={m.id} data-testid={`msg-row-${i}`} className="border-b border-zinc-100 hover:bg-zinc-50">
                      <Td className="text-zinc-500">{i + 1}</Td>
                      <Td className="font-medium">{m.name}</Td>
                      <Td>{m.email}</Td>
                      <Td>{m.subject}</Td>
                      <Td className="max-w-md text-zinc-600">{m.message}</Td>
                      <Td className="text-zinc-500 font-mono text-xs">{new Date(m.created_at).toLocaleString()}</Td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          )}
        </div>
      </main>
    </div>
  );
}

const StatCell = ({ label, value, icon: Icon, last }) => (
  <div className={`p-6 ${last ? "" : "border-r border-zinc-200"} border-b md:border-b-0 border-zinc-200`}>
    <Icon size={18} className="text-[#002FA7]" />
    <div className="font-display text-3xl mt-3">{value}</div>
    <div className="overline text-zinc-500 mt-1">{label}</div>
  </div>
);

const TabBtn = ({ active, onClick, children, testid }) => (
  <button
    onClick={onClick}
    data-testid={testid}
    className={`px-5 py-3 text-sm font-bold tracking-wider uppercase border-b-2 -mb-px transition-colors ${
      active ? "border-[#002FA7] text-[#002FA7]" : "border-transparent text-zinc-500 hover:text-zinc-800"
    }`}
  >
    {children}
  </button>
);

const Th = ({ children }) => <th className="text-left px-4 py-3 font-medium">{children}</th>;
const Td = ({ children, className = "" }) => <td className={`px-4 py-3 ${className}`}>{children}</td>;
